import { Component, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company } from 'identity-webapi-client';
import { GetIfcSettingsRequest, IfcToJsonSettings, SaveIfcSettingsRequest, SituWarpApiClient, IfcToJsonStoreySettings, IIfcToJsonStoreySettings, StoreyLevel } from 'situ-warpapi-client';
import { AuthService, BreadcrumbService, StateService, BannerService, BannerServiceStatus } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'app-ifc-storeys-config',
  templateUrl: './ifc-storeys-config.component.html',
  styleUrls: ['./ifc-storeys-config.component.less'],
})
export class IfcStoreysConfigComponent extends SituBaseComponent {
  selectedCompany: Company | undefined;
  storeyLevelEnum = StoreyLevel;
  storeyLevelKeyValues = Object.keys(StoreyLevel).filter(x => typeof StoreyLevel[x as any] === "number").map(key => ({ key: key, value: (StoreyLevel as any)[key] })).sort((a, b) => a.key.localeCompare(b.key));

  settings: IfcToJsonSettings;
  storeys: string[] = [];

  isNewStoreyFeed = true;
  storeySettings: IfcToJsonStoreySettings[] = [];
  newStoreySettings: ExtendedIfcToJsonStoreySettings[] = [];
  storeySettingsPageIndex = 0;
  pagedStoreySettings: IfcToJsonStoreySettings[] = [];
  newStoreySettingsPageIndex = 0;
  pagedNewStoreySettings: ExtendedIfcToJsonStoreySettings[] = [];

  artifactId: Guid | undefined;

  readonly pageSize = 200;

  private _loading = false;
  get loading() {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(this._loading);
  }

  get settingsjson() {
    return JSON.stringify(this.settings);
  }

  get isStoreySettingsDirty() {
    return JSON.stringify(this.settings.storeySettings) != JSON.stringify(this.storeySettings) || this.newStoreySettings.some(x => x.storeyLevel !== undefined);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private warpApiClient: SituWarpApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private bannerService: BannerService) {
      super(authService, stateService);
    this.settings = this.getNewSettings();
  }

  protected onSelectedCompanyChange(): void {
    this.loadSettings(this.selectedCompany?.code!);
  }

  loadSettings(companyCode: string) {
    const request = { companyCode: companyCode, includeStoreys: true } as GetIfcSettingsRequest;
    this.loading = true;
    this.warpApiClient.getIfcSettings(request).then((response) => {
      if (!response.success) {
        this.snackBar.open("Failed to load.");
      }

      this.artifactId = Guid.parse(response.artifactId!);

      const newSettings = this.getNewSettings();
      let settings = response.settings || newSettings;
      if (!settings.roomSettings) {
        settings.roomSettings = newSettings.roomSettings;
      }
      if (!settings.buildingMaterialSettings) {
        settings.buildingMaterialSettings = newSettings.buildingMaterialSettings;
      }
      if (!settings.storeySettings) {
        settings.storeySettings = newSettings.storeySettings;
      }
      if (!settings.furnishingSettings) {
        settings.furnishingSettings = newSettings.furnishingSettings;
      }

      this.settings = settings;
      this.storeySettings = JSON.parse(JSON.stringify(settings.storeySettings))?.map((x: any) => new IfcToJsonStoreySettings(x)).filter((x: IfcToJsonStoreySettings) => x.storeyLevel !== StoreyLevel.NotSet) || [];

      this.pagedStoreySettings = this.paginate(this.storeySettings || [], this.pageSize, this.storeySettingsPageIndex);

      this.storeys = response.storeys || [];

      const newStoreys = this.storeys.filter((storey) => !!storey && !this.storeySettings.some(y => y.storeyName == storey)) || [];

      this.newStoreySettings = newStoreys.map((storey) => new ExtendedIfcToJsonStoreySettings({ storeyName: storey, suggestedStoreyLevel: this.getSuggestedStoreyLevel(storey) }));
      this.pagedNewStoreySettings = this.paginate(this.newStoreySettings, this.pageSize, this.newStoreySettingsPageIndex);

      this.loading = false;
    }).catch((e) => {
      this.loading = false;
      this.snackBar.open("Failed to load.");
    });
  }

  getNewSettings() {
    let settings = new IfcToJsonSettings();
    settings.builder = this.selectedCompany?.code;
    settings.storeySettings = [];
    return settings;
  }

  onStoreysPageChange(event: PageEvent) {
    this.storeySettingsPageIndex = event.pageIndex;
    this.pagedStoreySettings = this.paginate(this.storeySettings, this.pageSize, this.storeySettingsPageIndex);
  }
  onNewStoreysPageChange(event: PageEvent) {
    this.newStoreySettingsPageIndex = event.pageIndex;
    this.pagedNewStoreySettings = this.paginate(this.newStoreySettings, this.pageSize, this.newStoreySettingsPageIndex);
  }

  toggleNewStoreySettings() {
    this.isNewStoreyFeed = !this.isNewStoreyFeed;
  }

  removeStoreySetting(storeySetting: IfcToJsonStoreySettings) {
    const index = this.storeySettings!.indexOf(storeySetting);
    let removedItems = this.storeySettings!.splice(index, 1);

    let removedExtendedItems = removedItems.map((storeySetting) =>
      new ExtendedIfcToJsonStoreySettings({ storeyName: storeySetting.storeyName, suggestedStoreyLevel: this.getSuggestedStoreyLevel(storeySetting.storeyName || "") }))
    this.newStoreySettings.push(...removedExtendedItems);

    this.pagedStoreySettings = this.paginate(this.storeySettings, this.pageSize, this.storeySettingsPageIndex);
    this.pagedNewStoreySettings = this.paginate(this.newStoreySettings, this.pageSize, this.newStoreySettingsPageIndex);

    this.snackBar.open("Removed storey setting!");
  }

  useSuggestedStoreyLevel(storeySetting: ExtendedIfcToJsonStoreySettings) {
    storeySetting.storeyLevel = storeySetting.suggestedStoreyLevel;
  }

  getSuggestedStoreyLevel(storey: string): StoreyLevel | undefined {
    return this.findKeyMatch(storey, this.storeyLevelKeyValues);
  }
  findKeyMatch(text: string, keyValues?: { key: string, value: any }[], dictionary?: { key: RegExp, value: any }[]): any {
    let results: { key: string | RegExp, value: any }[];
    // See if the supplied dictionary RegEx has any hits
    results = dictionary?.filter(x => x.key.test(text)) || [];
    if (results.length) {
      return results[0].value;
    }

    const searchText = text.toLowerCase().replace(/[0-9]/g, '').replace(' ', '').trim();
    if (searchText.length <= 3) {
      // Don't try and do a generic match on such a short string
      return;
    }

    // See if any of the type enums directly match the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim() == searchText) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if any of the type enums contains the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim().indexOf(searchText) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if the searchText contains any of the type enums
    results = keyValues?.filter(x => searchText.indexOf(x.key.toLowerCase().trim()) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }
  }
  getEnumKeyForDisplay(key: string) {
    if (key == null) {
      return '';
    }

    let display = key.replace(/([A-Z])/g, ' $1').trim();
    let words = display.split(' ');
    display = '';
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const nextWord = words[i + 1];
      display += word;
      if (word.length > 1 || nextWord?.length > 1) {
        display += ' ';
      }
    }

    display = display.trim();
    return display;
  }
  paginate(array: any[], pageSize: number, pageIndex: number) {
    return array.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
  }

  saveStoreySettings() {
    if (!this.settings) {
      return;
    }

    const request = { latestArtifactId: this.artifactId!.toString(), settings: new IfcToJsonSettings({ builder: this.settings.builder }) } as SaveIfcSettingsRequest;
    request.settings!.roomSettings = this.settings.roomSettings;
    request.settings!.buildingMaterialSettings = this.settings.buildingMaterialSettings;
    request.settings!.storeySettings = this.storeySettings.concat(this.newStoreySettings).filter(x => x.storeyLevel !== undefined && x.storeyLevel !== StoreyLevel.NotSet) || [];
    request.settings!.furnishingSettings = this.settings.furnishingSettings;

    this.loading = true;
    this.warpApiClient.saveIfcSettings(request).then((response) => {
      if (!response.success) {
        if (response.errorMessages && response.errorMessages.length > 0) {
          var message = `${response.errorMessages[0]}<br /><br />Please refresh the page and re-enter your changes again.`
          this.bannerService.AddMessage(message, BannerServiceStatus.error, this.artifactId, true);
        }
        this.snackBar.open("Failed to save storey settings.");
      }
      else {
        this.bannerService.RemoveMessage(this.artifactId!);
        this.snackBar.open("Saved storey settings!");

        this.artifactId = Guid.parse(response.artifactId!);

        this.settings.storeySettings = JSON.parse(JSON.stringify(request.settings?.storeySettings))?.map((x: any) => new IfcToJsonStoreySettings(x)) || [];
        this.storeySettings = request.settings?.storeySettings || [];

        this.pagedStoreySettings = this.paginate(this.storeySettings || [], this.pageSize, this.storeySettingsPageIndex);

        this.newStoreySettings = this.newStoreySettings.filter((storey) => !this.storeySettings.some(y => y.storeyName == storey.storeyName)) || [];

        if (this.newStoreySettings.length > 0 && this.newStoreySettings.length < (this.newStoreySettingsPageIndex * this.pageSize) + 1) {
          this.newStoreySettingsPageIndex = Math.floor(this.newStoreySettings.length / this.pageSize) - 1;
        }
        this.pagedNewStoreySettings = this.paginate(this.newStoreySettings, this.pageSize, this.newStoreySettingsPageIndex);
      }
      this.loading = false;
    }).catch(() => {
      this.loading = false;
      this.snackBar.open("Failed to save storey settings.");
    });
  }
}


class ExtendedIfcToJsonStoreySettings extends IfcToJsonStoreySettings {
  suggestedStoreyLevel?: StoreyLevel;

  constructor(data?: IExtendedIfcToJsonStoreySettings) {
    super(data);
  }
}
interface IExtendedIfcToJsonStoreySettings extends IIfcToJsonStoreySettings {
  suggestedStoreyLevel?: StoreyLevel;
}
